import { useState } from "react";
import { createDirectPass, createInvite } from "../admin/api";
import { getCompanySettings, listHostsDirectory } from "./api";
import { useAdminResource } from "../admin/use-admin-resource";
import { useToast } from "../../components/useToast";
import { useAuth } from "../auth/useAuth";
import { INVALID_PHONE_MESSAGE, isValidOptionalPhone } from "../../lib/validation";

type InviteForm = {
  fullName: string;
  email: string;
  phone: string;
  hostId: string;
  purpose: string;
  expectedExit: string;
  directEntry: boolean;
};

const INITIAL_FORM: InviteForm = {
  fullName: "",
  email: "",
  phone: "",
  hostId: "",
  purpose: "",
  expectedExit: "",
  directEntry: false,
};

export function InviteVisitorForm() {
  const { session } = useAuth();
  const { showSuccess, showError } = useToast();
  const { data: hosts } = useAdminResource(() => listHostsDirectory(), []);
  const { data: settings } = useAdminResource(() => getCompanySettings(), []);
  const directEntryAllowed = !!settings?.allow_host_direct_entry;

  const [form, setForm] = useState<InviteForm>(INITIAL_FORM);
  const [pending, setPending] = useState(false);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultKind, setResultKind] = useState<"invite" | "pass">("invite");
  const [emailSent, setEmailSent] = useState(false);
  const [whatsappSent, setWhatsappSent] = useState(false);

  const myHostId = hosts?.find((h) => h.username === session?.username)?.id;

  async function submit() {
    const hostId = form.hostId ? Number(form.hostId) : myHostId;
    if (!hostId) {
      showError("Select a host for this visit");
      return;
    }
    if (!form.purpose.trim()) {
      showError("Purpose of visit is required");
      return;
    }

    const direct = directEntryAllowed && form.directEntry;
    if (direct && !form.fullName.trim()) {
      showError("Enter the visitor's name for a direct-entry pass");
      return;
    }
    if (!direct && !form.email.trim()) {
      showError("Visitor email is required to send an invite");
      return;
    }
    if (!isValidOptionalPhone(form.phone)) {
      showError(INVALID_PHONE_MESSAGE);
      return;
    }

    setPending(true);
    setResultUrl(null);
    try {
      if (direct) {
        const result = await createDirectPass({
          full_name: form.fullName.trim(),
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          host_id: hostId,
          purpose: form.purpose.trim(),
          expected_exit: form.expectedExit || undefined,
        });
        setResultKind("pass");
        setResultUrl(result.pass_url);
        setEmailSent(result.email_sent);
        setWhatsappSent(result.whatsapp_sent);
        showSuccess(
          result.whatsapp_sent
            ? "Gate pass sent on WhatsApp"
            : result.email_sent
              ? `Gate pass emailed to ${result.email}`
              : "Gate pass created — share the link below with the visitor",
        );
      } else {
        const result = await createInvite({
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          host_id: hostId,
          purpose: form.purpose.trim(),
          expected_exit: form.expectedExit || undefined,
        });
        setResultKind("invite");
        setResultUrl(result.invite_url);
        setEmailSent(result.email_sent);
        setWhatsappSent(false);
        showSuccess(
          result.email_sent
            ? `Invite emailed to ${result.email}`
            : `Invite created for ${result.email} (email not sent — SMTP not configured)`,
        );
      }
      setForm((prev) => ({ ...INITIAL_FORM, directEntry: prev.directEntry }));
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to submit");
    } finally {
      setPending(false);
    }
  }

  async function copyLink() {
    if (!resultUrl) return;
    try {
      await navigator.clipboard.writeText(resultUrl);
      showSuccess("Link copied");
    } catch {
      showError("Couldn't copy — select and copy it manually");
    }
  }

  const direct = directEntryAllowed && form.directEntry;

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Invite a Visitor</h2>
        <span>{direct ? "Direct pass" : "Email link"}</span>
      </header>
      <form
        className="action-form"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {directEntryAllowed ? (
          <label className="checkbox-field" style={{ marginBottom: "0.5rem" }}>
            <input
              type="checkbox"
              checked={form.directEntry}
              onChange={(event) => setForm((prev) => ({ ...prev, directEntry: event.target.checked }))}
            />
            Direct entry — issue the pass now (visitor skips the detail form)
          </label>
        ) : null}

        <div className="checkin-fields">
          {direct ? (
            <input
              value={form.fullName}
              onChange={(event) => setForm((prev) => ({ ...prev, fullName: event.target.value }))}
              placeholder="Visitor name *"
              aria-label="Visitor name"
            />
          ) : null}
          <input
            type="email"
            value={form.email}
            onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
            placeholder={direct ? "Visitor email (to send the pass)" : "Visitor email *"}
            aria-label="Visitor email"
          />
          <input
            type="tel"
            inputMode="tel"
            value={form.phone}
            onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
            placeholder={direct ? "Visitor mobile (to send the pass on WhatsApp)" : "Visitor mobile (optional)"}
            aria-label="Visitor mobile"
          />
          <select
            className="search-input"
            value={form.hostId}
            onChange={(event) => setForm((prev) => ({ ...prev, hostId: event.target.value }))}
            aria-label="Host"
          >
            <option value="">{myHostId ? "Me (default host)" : "Select host *"}</option>
            {(hosts ?? []).map((host) => (
              <option key={host.id} value={host.id}>
                {host.name}
              </option>
            ))}
          </select>
          <input
            value={form.purpose}
            onChange={(event) => setForm((prev) => ({ ...prev, purpose: event.target.value }))}
            placeholder="Purpose of visit *"
            aria-label="Purpose of visit"
            required
          />
          <input
            type="datetime-local"
            value={form.expectedExit}
            onChange={(event) => setForm((prev) => ({ ...prev, expectedExit: event.target.value }))}
            aria-label="Expected exit time"
          />
        </div>
        <button className="refresh-btn action-submit" type="submit" disabled={pending}>
          {pending ? "Working..." : direct ? "Create Gate Pass" : "Send Invite"}
        </button>
      </form>
      {resultUrl ? (
        <div className="tool-card">
          <p>
            {resultKind === "pass"
              ? whatsappSent
                ? "Gate pass sent on WhatsApp. Share the pass link if needed:"
                : emailSent
                  ? "Gate pass emailed. Share the pass link if needed:"
                  : "Gate pass created — share this pass link with the visitor:"
              : emailSent
                ? "Invite emailed. Share the link if needed:"
                : "Email not sent (SMTP not configured) — share this link directly:"}
          </p>
          <div className="action-cell">
            <code style={{ wordBreak: "break-all" }}>{resultUrl}</code>
            <button type="button" className="refresh-btn" onClick={() => void copyLink()}>
              Copy Link
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
}
